import { z } from 'zod';

/**
 * ADR-030 — per-project-type overrides for methodology defaults on an
 * assumption profile.
 *
 * Stored as a JSONB column (`assumption_profiles.project_type_overrides`)
 * with DB default `{}`. All sub-fields are optional — missing values fall
 * through to the base profile's value. `new_startup` always reads the
 * base values and has no override block.
 *
 * Edits to this blob flow through the same ADR-029 Phase 2 profile_change
 * authorization pipeline as any other assumption-profile edit.
 */

const rate01 = z.number().min(0).max(1);
const rateUnbounded = z.number().min(0).max(2);

/** Single per-type override block (expansion or retention). */
export const ProjectTypeOverrideBlockSchema = z
  .object({
    displacementRate: rate01.optional(),
    workerRelocationPct: rate01.optional(),
    staffingRampYear1: rate01.optional(),
    staffingRampYear2: rate01.optional(),
    revenueRampYear1: rate01.optional(),
    revenueRampYear2: rate01.optional(),
    realPropertyAppreciationRate: rateUnbounded.optional(),
    /**
     * ADR-031 Part D — retention-only default for the wizard's
     * `retentionCounterfactual.probabilityOfDeparture`. Ignored on
     * expansion blocks (the override block shape is shared). Ships at
     * 0.5 in STARTER_PROJECT_TYPE_OVERRIDES — a deliberate conservative
     * midpoint that requires analysts to move the knob consciously.
     */
    defaultProbabilityOfDeparture: rate01.optional(),
  })
  .strict();

export type ProjectTypeOverrideBlock = z.infer<typeof ProjectTypeOverrideBlockSchema>;

/**
 * Full overrides blob. Shape: `{ expansion?: {...}, retention?: {...} }`.
 * `new_startup` intentionally has no slot — the base profile IS the
 * new_startup default.
 */
export const ProjectTypeOverridesSchema = z
  .object({
    expansion: ProjectTypeOverrideBlockSchema.optional(),
    retention: ProjectTypeOverrideBlockSchema.optional(),
  })
  .strict()
  .default({});

export type ProjectTypeOverrides = z.infer<typeof ProjectTypeOverridesSchema>;

/**
 * Recommended starter defaults per ADR-030 §Recommended starter defaults.
 * Shipped as seed data; owners may edit via the methodology settings page
 * (guarded by profile_change authorization).
 */
export const STARTER_PROJECT_TYPE_OVERRIDES: Required<ProjectTypeOverrides> = {
  expansion: {
    displacementRate: 0.2,
    workerRelocationPct: 0.1,
    staffingRampYear1: 0.75,
    staffingRampYear2: 1.0,
    revenueRampYear1: 0.75,
    revenueRampYear2: 1.0,
    realPropertyAppreciationRate: 0.02,
  },
  retention: {
    displacementRate: 0.0,
    workerRelocationPct: 0.0,
    staffingRampYear1: 1.0,
    staffingRampYear2: 1.0,
    revenueRampYear1: 1.0,
    revenueRampYear2: 1.0,
    realPropertyAppreciationRate: 0.0,
    // ADR-031 Part D — conservative midpoint; analysts must move this
    // knob deliberately in either direction. Evidence required > 0.5.
    defaultProbabilityOfDeparture: 0.5,
  },
};

/**
 * Resolve the effective methodology values for a given project type by
 * layering the override block on top of a base set. Missing override keys
 * pass through from the base — so an owner who only customizes
 * `retention.staffingRampYear1` still gets base values for everything else.
 *
 * `new_startup` always returns the base unchanged.
 */
export function resolveEffectiveOverrides<T extends ProjectTypeOverrideBlock>(
  base: T,
  overrides: ProjectTypeOverrides | null | undefined,
  projectType: 'new_startup' | 'expansion' | 'retention',
): T {
  if (projectType === 'new_startup') return base;
  const block = overrides?.[projectType];
  if (!block) return base;
  return { ...base, ...block };
}
